import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

export type InputContextProps = {
  inputValue: string;
  setInputValue: Dispatch<SetStateAction<string>>;
};

export type InputContextType = InputContextProps & {
  inputRef: React.RefObject<HTMLInputElement>;
  inputValue: string;
  inputValueRef: React.RefObject<string>;
  noResults: boolean;
  setInputValue: Dispatch<SetStateAction<string>>;
  setNoResults: Dispatch<SetStateAction<boolean>>;
  setSuggestionQuery: Dispatch<SetStateAction<string>>;
  suggestionQuery: string;
};

const defaultContext: InputContextType = {
  inputRef: { current: null },
  inputValue: '',
  inputValueRef: { current: '' },
  noResults: false,
  setInputValue() {},
  setNoResults() {},
  setSuggestionQuery() {},
  suggestionQuery: ''
};

const InputContext = createContext<InputContextType>(defaultContext);

export type OwnProps = PropsWithChildren<InputContextProps>;

export function InputContextProvider({ children, ...props }: OwnProps) {
  const { inputValue } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const inputValueRef = useRef(inputValue);
  const [noResults, setNoResults] = useState(false);
  const [suggestionQuery, setSuggestionQuery] = useState('');

  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  const value = useMemo<InputContextType>(
    () => ({
      ...props,
      inputRef,
      inputValueRef,
      noResults,
      suggestionQuery,
      setNoResults,
      setSuggestionQuery
    }),
    [noResults, props, suggestionQuery]
  );

  return (
    <InputContext.Provider value={value}>{children}</InputContext.Provider>
  );
}

export default InputContext;

export function useInputContext() {
  return useContext(InputContext);
}
